/**
 * Objects with unknown keys and values.
 */
export interface MetadataObj {
    [x: string]: any;
}

const isObjectLike = (value: any) => typeof value === "object" && value !== null;

const getTag = (value: any) => {
    if (value === undefined) {
        return "[object Undefined]";
    }
    if (value === null) {
        return "[object Null]";
    }

    return toString.call(value);
};

/**
 * Checks if the value is actually an object.
 */
const isObject = (value: any) => {
    if (!isObjectLike(value) || getTag(value) !== "[object Object]") {
        return false;
    }

    if (Object.getPrototypeOf(value) === null) {
        return true;
    }

    let proto = value;
    while (Object.getPrototypeOf(proto) !== null) {
        proto = Object.getPrototypeOf(proto);
    }
    return Object.getPrototypeOf(value) === proto;
};

/**
 * Extracts an object from an array of objects.
 * * If the array is empty returns an empty object.
 * * If the matcher finds no match returns an empty object.
 * * If no matcher is passed returns the first item in the array.
 */
export const getObjectFromArray = (
    array: any[],
    key?: string,
    value?: any
) => (Array.isArray(array) && array.find((item: any) => (key && value ? item[key] === value : item))) || {};

/**
 * Gets the value deep within a path of object.
 * If at any point in the path the key does not exist. We return undefined.
 * If we have a default value and the path does not exist return that default value.
 *
 * This allows you to avoid constantly checking for properties along the way like
 * `this.state.wave && this.state.wave.data && this.state.wave.data.companyName`
 *
 * @example get(this, "state.wave.data.companyName") // can return undefined
 * @example get(this, "state.companyOptions", []); // always returns an array.
 */
export const get = (target: MetadataObj, path: string, defaultValue?: any): any => {
    const targetObj = isObject(target) ? target : {};
    const result = path.split(".").reduce((obj: MetadataObj, key: string) => (obj || {})[key], targetObj);

    return [undefined, null].includes(result) ? defaultValue : result;
};
